import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Lock, CheckCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_META = {
  office_hours: {
    title: "AI-Investor Office Hours",
    blurb:
      "This is an invite-only feature. Leave your email and we'll get in touch when you're granted access.",
    accent: "emerald",
  },
  event_intel: {
    title: "Event Investor Intelligence",
    blurb:
      "This is an invite-only feature. Leave your email and we'll notify you as soon as access is available.",
    accent: "cyan",
  },
  aspiring: {
    title: "Operon Start — Aspiring Founders",
    blurb:
      "Operon Start is currently invite-only. Share your email and we'll reach out with next steps.",
    accent: "purple",
  },
};

export function RequestAccessModal({ open, onOpenChange, feature }) {
  const meta = FEATURE_META[feature] || FEATURE_META.aspiring;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setSubmitted(false);
      setError("");
      setSubmitting(false);
    }
  }, [open, feature]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/feature-access-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, feature }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-slate-700 sm:max-w-md"
        data-testid={`request-access-modal-${feature}`}
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            {meta.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {meta.blurb}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="mt-4 space-y-4" data-testid="request-access-success">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Thanks — we&apos;ve got your request.</p>
                <p className="text-slate-400 text-sm mt-1">
                  Our team has been notified and will get back to you shortly.
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white"
              data-testid="request-access-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Your email</label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-11"
                autoFocus
                required
                data-testid="request-access-email-input"
              />
              {error && (
                <p className="text-red-400 text-sm" data-testid="request-access-error">
                  {error}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                disabled={submitting}
                data-testid="request-access-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !email}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="request-access-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Request Access"
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RequestAccessModal;
